/** @file This file contains the code to persist the store. */
import moment from 'moment';
import * as codec from 'purify-ts/Codec';
import * as apiModels from '@codegrade/api';
import { Just, Maybe } from '@codegrade/maybe';
import { NonEmptyList } from '@codegrade/non-empty-list';
import { errors, parsers } from '@codegrade/utils';
import { NOT_LOGGED_IN_TOAST_TAG } from '@/constants';
import { LoggedInUser } from '@/models/user';
import { getDefaultGlobalPermissions } from '@/permissions';
import router from '@/router';
import { signal } from '@/utils';
import { UserStore } from './modules/user';
/** A parser for a session. */
const loggedInUserSessionParser = codec.Codec.interface({
    token: codec.string,
    restrictions: apiModels.SessionRestrictionData,
    expiresAt: parsers.moment,
});
/** Parser for the method the user used to log into the app. */
const loginMethodParser = parsers.TaggedUnionBuilder.create('tag')
    .add('username-password', codec.Codec.interface({
    tenantId: parsers.maybe(codec.string),
}))
    .add('lti', codec.Codec.interface({
    lms: parsers.maybe(codec.string),
}))
    .add('sso', codec.Codec.interface({
    tenantId: parsers.maybe(codec.string),
}))
    .add('assignment-login', codec.Codec.interface({
    assignmentId: codec.number,
    loginUuid: codec.string,
}))
    .build();
/** Is persistence currently disabled? */
let disabledPersistance = false;
/** The toast message to show, if any, about failing to setup local storage. */
let toastMessage = null;
/** Did we have an error setting up local storage. */
let localStorageError = false;
/** Was the user session not loaded because it expired. */
let sessionExpired = false;
/** The current status of persistence. */
export var PersistanceStatus;
(function (PersistanceStatus) {
    PersistanceStatus["ENABLED"] = "ENABLED";
    PersistanceStatus["DISABLED"] = "DISABLED";
})(PersistanceStatus || (PersistanceStatus = {}));
/** @returns Get the status of the persistence. */
export function getPersistanceStatus() {
    return disabledPersistance ? PersistanceStatus.DISABLED : PersistanceStatus.ENABLED;
}
try {
    window.localStorage.setItem('vuex', '""');
    window.localStorage.removeItem('vuex');
    window.localStorage.setItem('@@', '1');
    if (window.localStorage.getItem('@@') !== '1') {
        throw new Error('Localstorage did not save state');
    }
    window.localStorage.removeItem('@@');
}
catch (e) {
    localStorageError = true;
    toastMessage = `Unable to persistently store user credentials, please check
        you browser privacy levels. You will not be logged-in in other tabs or
        when reloading.`;
}
/** @returns If we should use local storage. */
function useLocalStorage() {
    return !disabledPersistance && !localStorageError;
}
/** A value in local storage. */
class LocalStorageValue {
    /** The key to safe the value under. */
    key;
    /**
     * @param ns The namespace part of the key.
     * @param key The key part of the key.
     */
    constructor(ns, key) {
        this.key = `CG_PERSIST-${ns}|${key}`;
    }
    /** @returns The JSON parsed value from local storage. */
    get() {
        return Maybe.fromNullable(window.localStorage.getItem(this.key))
            .chain(val => Maybe.encase(() => JSON.parse(val)))
            .chainNullable(x => x);
    }
    /** Clear the value from local storage. */
    clear() {
        window.localStorage.removeItem(this.key);
    }
    /**
     * Set a value in local storage.
     *
     * @param value The value, this should be JSON.
     */
    set(value) {
        window.localStorage.setItem(this.key, value);
    }
}
/** A simple persisted value without any transformation. */
class SimplePersistance {
    ns;
    path;
    parser;
    /** The underlying way to persist the value. */
    storage;
    /**
     * @param ns The namespace of the value.
     * @param path They path of the value.
     * @param parser The parser for the value from parsed json data.
     */
    constructor(ns, path, parser) {
        this.ns = ns;
        this.path = path;
        this.parser = parser;
        this.storage = new LocalStorageValue(ns, path);
        Object.freeze(this);
    }
    /** @inheritdoc */
    getValue(state) {
        return state[this.ns][this.path];
    }
    /** @returns The value from the store, if found and can be parsed. */
    retrieveValue() {
        return this.storage.get().chain(val => Maybe.fromEither(this.parser.decode(val)));
    }
    /** @inheritdoc */
    storeValue(value) {
        this.storage.set(JSON.stringify(value));
    }
    /** @inheritdoc */
    retrieveAndSetValue(state) {
        this.retrieveValue().ifJust(value => {
            state[this.ns] = {
                ...state[this.ns],
                [this.path]: value,
            };
        });
    }
    /** @inheritdoc */
    clear() {
        this.storage.clear();
    }
}
/** The class to persist the currently logged in user. */
class UserPersitance {
    /** The key used for the old JWT data. */
    oldJWTKey = 'CG_PERSIST-user|user';
    /** The key used for storing the session data. */
    localStorageKey = 'CG_PERSIST-user|user-session';
    /** The key used for storing the session data. */
    sessionStorageKey = 'CG_PERSIST-user|user-sessions';
    /** The parser for the session storage data. */
    static sessionStorageParser = codec.Codec.interface({
        sessions: codec.array(loggedInUserSessionParser),
    });
    /** The parser for the local storage data. */
    static localStorageParser = codec.Codec.interface({
        id: codec.number,
        tenantId: codec.nullable(codec.string),
        session: loggedInUserSessionParser,
        loginMethod: parsers.nonFailing(loginMethodParser),
    });
    /** @inheritdoc */
    getValue(state) {
        return state.user.loggedInUser.mapOrDefault((user) => ({
            localStorage: {
                id: user.id,
                tenantId: user.tenantId.extractNullable(),
                session: user.sessions[0],
                loginMethod: user.loginMethod,
            },
            sessionStorage: {
                sessions: user.sessions.slice(1),
            },
        }), null);
    }
    /**
     * Store the current value.
     *
     * @param value The value to store.
     */
    storeValue(value) {
        window.localStorage.removeItem(this.oldJWTKey);
        window.localStorage.setItem(this.localStorageKey, JSON.stringify(UserPersitance.localStorageParser.encode(value.localStorage)));
        window.sessionStorage.setItem(this.sessionStorageKey, JSON.stringify(UserPersitance.sessionStorageParser.encode(value.sessionStorage)));
    }
    /** Clear the data from the store. */
    clear() {
        window.localStorage.removeItem(this.oldJWTKey);
        window.localStorage.removeItem(this.localStorageKey);
        window.sessionStorage.removeItem(this.sessionStorageKey);
    }
    /**
     * Retrieve the user and set it in the root state.
     *
     * @param state The state in which the user will be set if it can be found.
     */
    retrieveAndSetValue(state) {
        window.localStorage.removeItem(this.oldJWTKey);
        const user = Maybe.fromNullable(window.localStorage.getItem(this.localStorageKey))
            .map((str) => JSON.parse(str))
            .chain(parsed => Maybe.fromEither(UserPersitance.localStorageParser.decode(parsed)))
            .extract();
        if (user == null) {
            return;
        }
        const tempSessions = Maybe.fromNullable(window.sessionStorage.getItem(this.sessionStorageKey))
            .map((str) => JSON.parse(str))
            .chain(parsed => Maybe.fromEither(UserPersitance.sessionStorageParser.decode(parsed)))
            .mapOrDefault(u => u.sessions, []);
        const now = moment.utc();
        const sessions = [user.session, ...tempSessions].filter(session => session.expiresAt.isAfter(now));
        if (!NonEmptyList.isNonEmpty(sessions)) {
            this.clear();
            sessionExpired = true;
            return;
        }
        const loggedInUser = new LoggedInUser({
            id: user.id,
            type: 'normal',
            isTestStudent: false,
            name: Maybe.empty(),
            tenantId: Maybe.fromNullable(user.tenantId).filter(tId => tId !== ''),
            username: '',
        }, {
            sessions,
            loginMethod: user.loginMethod,
            canSeeHidden: false,
            permissions: getDefaultGlobalPermissions(),
            email: Maybe.empty(),
            fullyLoaded: false,
        });
        state.user = {
            ...state.user,
            loggedInUser: new Just(loggedInUser),
        };
    }
}
const valuesToPersist = [
    new SimplePersistance('pref', 'fontSize', codec.number),
    new SimplePersistance('pref', 'contextAmount', codec.number),
    new SimplePersistance('ui_prefs', 'darkMode', codec.boolean),
    new UserPersitance(),
];
/**
 * Integrates a persistence mechanism with a store to automatically persist selected parts of the
 * store's state.
 *
 * It sets up a watcher on the store that triggers on state changes. Depending on the current
 * environment's storage capabilities and the value derived from the state, it either stores the new
 * value or clears the existing stored value.
 *
 * @param store The store on which to set up the state watcher.
 * @param toPersist The persistence handler that defines how to get, store, or clear values from the
 * store.
 */
function enablePersist(store, toPersist) {
    store.watch(state => toPersist.getValue(state), value => {
        if (useLocalStorage()) {
            if (value == null) {
                toPersist.clear();
            }
            else {
                toPersist.storeValue(value);
            }
        }
    });
}
/**
 * Enable the persistence for the store.
 *
 * @param store The store that should be persisted.
 */
export function enablePersistance(store) {
    for (const value of valuesToPersist) {
        enablePersist(store, value);
    }
}
/** Disabled the persistance of data in local storage. */
export function disablePersistance() {
    sessionExpired = false;
    let error;
    if (!useLocalStorage()) {
        disabledPersistance = true;
        return;
    }
    valuesToPersist.forEach(persist => {
        // Even on an error we try to clear all the remaining keys.
        try {
            persist.clear();
        }
        catch (e) {
            error = errors.coerceToError(e);
        }
    });
    if (error != null) {
        throw error;
    }
    disabledPersistance = true;
}
/**
 * Called when the vue root is created.
 *
 * @param root The newly created root vue.
 */
export function onVueCreated(root) {
    // Do this in a timeout so that when we disable persistence when launching
    // in LTI we do not show this warning message as it makes no sense.
    setTimeout(() => {
        if (!disabledPersistance && toastMessage != null) {
            root.$bvToast.toast(toastMessage, {
                title: 'Warning',
                variant: 'warning',
                toaster: 'b-toaster-top-right',
                noAutoHide: true,
                solid: true,
            });
        }
    }, 1000);
    // If we loaded an expired session (and thus not actually loaded it), we should warn the user,
    // but only if we are on a page that needs a login.
    if (sessionExpired) {
        sessionExpired = false;
        if (router.currentRoute?.meta?.loginRequired === true) {
            root.$addToast({
                tag: NOT_LOGGED_IN_TOAST_TAG,
                title: 'Not logged in',
                message: 'Your session has expired. Please log in again to view this page.',
                variant: 'danger',
            });
            root.$router.logoutAndRedirectAfterLogin();
        }
    }
}
signal.STORE_CREATED.register(async (store) => {
    if (useLocalStorage()) {
        const newState = valuesToPersist.reduce((acc, persist) => {
            persist.retrieveAndSetValue(acc);
            return acc;
        }, { ...store.state });
        store.replaceState(newState);
        enablePersistance(store);
        const loggedIn = store.state.user.loggedInUser;
        if (loggedIn.isJust()) {
            await Promise.all([
                signal.AFTER_USER_CHANGED.sendPromise(store.state.user.loggedInUser),
                // We store incomplete users in localstorage, in this case we should load the user
                // when the page loads.
                loggedIn.value.fullyLoaded ? Promise.resolve() : UserStore.verifyLogin(),
            ]);
        }
    }
});
